import { PortfolioVersionType } from '@/constants/PortfolioVersionType';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { createGlobalState } from '@vueuse/shared';
import type { RouteLocationNormalized } from 'vue-router';
import useRouteChecks from './useRouteChecks';
import { useFeatureFlag } from './useFeatureFlag';
import { useHistoryMode } from './useConstituentRiskUtilities';
import { useItemUnderAnalysisGlobalState } from './useItemUnderAnalysis';

// We don't use useLocalStorage here because we want to reset to default values after a refresh
const usePortfolioVersionStateGlobalState = createGlobalState(
  (): {
    /**
     * Set only when explicitly selected by the user
     * or reset to null by app-specific logic.
     * This is the only version type that should be written to.
     *
     * Simulation portfolio case:
     * Forced to Simulation, when requested version does not match received version type to prevent refetching
     */
    versionTypeOverride: Ref<PortfolioVersionType | null>;
  } => {
    return {
      versionTypeOverride: ref<PortfolioVersionType | null>(null),
    };
  },
);

export function usePortfolioVersionTypeGlobalState(): {
  versionType: ComputedRef<PortfolioVersionType | null>;
  isLiveVersionType: ComputedRef<boolean | null>;
  isSimulationVersionType: ComputedRef<boolean | null>;
  isProvisionalVersionType: ComputedRef<boolean | null>;
  versionTypeOverride: Ref<PortfolioVersionType | null>;
  derivedVersionType: ComputedRef<PortfolioVersionType | null>;
} {
  const { versionTypeOverride } = usePortfolioVersionStateGlobalState();
  const { defaultModeDraft } = useItemUnderAnalysisGlobalState();

  /**
   * Version type derived from the draft API response.
   * Returns `null` when no draft is available.
   * This temporary `null` is expected right after `rootItemIdentifier` changes, until the new
   * draft is fetched.
   */
  const derivedVersionType = computed((): PortfolioVersionType | null => {
    const draft = defaultModeDraft.value;
    if (!draft) return null;

    return draft.portfolioTree.version ?? PortfolioVersionType.SIMULATION;
  });

  /**
   * Primary version type to read when determining a portfolio version.
   * Uses `versionTypeOverride` when present; otherwise falls back to `derivedVersionType`.
   */
  const versionType = computed(
    (): PortfolioVersionType | null => versionTypeOverride.value ?? derivedVersionType.value,
  );

  const isLiveVersionType = computed((): boolean | null => {
    if (!versionType.value) return null;

    return versionType.value === PortfolioVersionType.POSITION || versionType.value === PortfolioVersionType.HISTORY;
  });

  const isSimulationVersionType = computed((): boolean | null => {
    if (!versionType.value) return null;

    return versionType.value === PortfolioVersionType.SIMULATION;
  });

  const isProvisionalVersionType = computed((): boolean | null => {
    if (!versionType.value) return null;

    return versionType.value === PortfolioVersionType.PROVISIONAL;
  });

  return {
    versionType,
    isLiveVersionType,
    isSimulationVersionType,
    isProvisionalVersionType,
    versionTypeOverride,
    derivedVersionType,
  };
}

/**
 * Because the "Live Version" option encompasses both Position and History versions,
 * we need to determine which version to set based on the current route.
 * The API handles sending the correct draft version,
 * such as returning "Simulation" version if a live version is requested for a non-live portfolio.
 */
export function useGetLivePortfolioVersionTypeToSet({
  route,
}: {
  route: RouteLocationNormalized;
}): Ref<PortfolioVersionType> {
  const { hasHistoryVersionAccess, hasRiskHistoryAccess } = useFeatureFlag();
  const { isOnConstituentRisk, isOnPosition } = useRouteChecks(route);
  const isHistoryMode = useHistoryMode();

  return computed((): PortfolioVersionType => {
    // until such time that the History version feature is complete, most users will only ever use Position version if in Live
    // using both hasHistoryVersionAccess and hasRiskHistoryAccess so that the product can QA the risk history feature with ease
    if (!hasHistoryVersionAccess.value && !hasRiskHistoryAccess.value) return PortfolioVersionType.POSITION;

    if (isOnConstituentRisk.value) {
      if (isHistoryMode.value) return PortfolioVersionType.HISTORY;

      return PortfolioVersionType.POSITION;
    }

    if (isOnPosition.value) return PortfolioVersionType.POSITION;

    return PortfolioVersionType.HISTORY;
  });
}
